import stringify from 'fast-json-stable-stringify';
import { gzipSync } from 'fflate';

import { getDocument } from '@atlaskit/browser-apis';
import { fg } from '@atlaskit/platform-feature-flags/fg';
import type { ExtensionViewData } from '@atlassian/forge-ui-types';

import type { GQLExtension } from '../../../web-client/graphql/types';
import { getCrossDomainEmbeddingOrigin } from './getCrossDomainEmbeddingOrigin';

export const getEncodedAppContext = (
	extension: GQLExtension,
	extensionViewData?: ExtensionViewData,
): string => {
	const { egress = [], installationConfig = [] } = extension;

	const getMacroParentHostDomain = () => {
		const doc = getDocument(); // browser stips off referer and origin.
		const { hostname, pathname } = window.location;
		// Allow all origins for local Confluence development where the FAUS ADF renderer
		// runs at /forge-apps/adf-renderer and the browser strips off the referrer/origin.
		if (hostname === 'localhost' && pathname === '/forge-apps/adf-renderer') {
			return '*';
		}
		if (doc?.referrer && extension.type === 'xen:macro') {
			const referrerUrl = new URL(doc.referrer);
			const hostnameParts = referrerUrl.hostname.split('.');
			// Check if the second subdomain is 'cdn' to confirm it's a CDN URL
			const isCdnUrl = hostnameParts.length > 1 && hostnameParts[1] === 'cdn';
			if (isCdnUrl) {
				return hostnameParts[0];
			}

			const isLocalHost = referrerUrl.hostname === 'localhost';

			if (isLocalHost) {
				return referrerUrl.host;
			}
		}
	};

	const macroParentHostDomain = getMacroParentHostDomain();

	const shouldIncludeConnectMacroParentDomain =
		Boolean(extensionViewData?.connectMacroOrigin) && fg('forge_nbm_set_frame_ancestors');

	// Resolve the cross-domain embedding origin directly off the embedding (host) window so the
	// Forge CDN can add it to the iframe's `frame-ancestors` CSP; without it the iframe is blocked
	// when the host page is embedded cross-domain (e.g. on a custom domain). Reading it here — rather
	// than threading it via `extensionViewData` — covers every extension type and runtime, not just
	// macros. Keep the whole resolver behind the gate so the new code path stays fully killable.
	const crossDomainEmbeddingOrigin = fg('forge-ui-embedded-confluence-custom-domain-csp')
		? getCrossDomainEmbeddingOrigin()
		: undefined;

	const contextPayload = {
		egress,
		installationConfig,
		hostname: window.location.hostname,
		...(macroParentHostDomain
			? { macroParentHostDomain, macroParentHost: macroParentHostDomain }
			: {}),
		...(shouldIncludeConnectMacroParentDomain
			? { connectMacroParentDomain: extensionViewData?.connectMacroOrigin }
			: {}),
		...(crossDomainEmbeddingOrigin ? { parentEmbeddingDomain: crossDomainEmbeddingOrigin } : {}),
	};
	return gzipAndB64Encode(contextPayload);
};

const gzipAndB64Encode = (json: Record<string, any>): string => {
	const jsonString = stringify(json); // We use fast-json-stable-stringify to ensure that the JSON is deterministic

	try {
		const zippedData = gzipSync(new TextEncoder().encode(jsonString), {
			level: 9, // Max compression,
			mtime: 0, // We set the mtime to 0 to ensure that the output is deterministic
		});
		return btoa(String.fromCharCode(...zippedData));
	} catch (err: unknown) {
		// eslint-disable-next-line no-console
		console.error((err as Error).message);
	}

	return '';
};
